import { useCallback, useEffect, useState } from "react"
import type { SortingState } from "@tanstack/react-table"

import { api, type AvisoNaTela } from "../api"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Checkbox } from "../componentes/Checkbox"
import { Empty, EmptyContent, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Escolha } from "../componentes/Escolha"
import * as Icones from "../componentes/Icones"
import { FalhaNaSecao } from "../componentes/Secao"
import { Selo, SeloDoValor } from "../componentes/Selo"
import { Column, TablePrincipal2 } from "../componentes/TablePrincipal2"
import { EsqueletoDeTabela } from "../ficha/Pedido"
import { COLUNA_DE_ACOES } from "../ficha/Tabela"
import { dataHora, plural } from "../formato"
import { MAIUSCULA } from "../lib/classes"
import { cn } from "../lib/cn"

type Ordenavel = "data" | "gravidade" | "contrato"

const GRAVIDADES = [
  { valor: "critico", rotulo: "Crítico" },
  { valor: "atencao", rotulo: "Atenção" },
  { valor: "informacao", rotulo: "Informação" },
]

const NA_TABELA: Record<string, Ordenavel> = { criado_em: "data", gravidade: "gravidade", contrato_numero: "contrato" }
const DA_TABELA: Record<Ordenavel, string> = { data: "criado_em", gravidade: "gravidade", contrato: "contrato_numero" }

export function Avisos({ aoAbrirContrato }: { aoAbrirContrato: (id: number) => void }) {
  const [avisos, definirAvisos] = useState<AvisoNaTela[] | null>(null)
  const [naoLidos, definirNaoLidos] = useState(0)
  const [erro, definirErro] = useState<string | null>(null)
  const [soNaoLidos, definirSoNaoLidos] = useState(false)
  const [gravidade, definirGravidade] = useState("")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(20)
  const [total, definirTotal] = useState(0)
  const [ordenarPor, definirOrdenarPor] = useState<Ordenavel | null>(null)
  const [ordem, definirOrdem] = useState<"asc" | "desc">("desc")

  const carregar = useCallback(() => {
    definirErro(null)
    api
      .avisos(soNaoLidos, 100, { pagina, porPagina }, {
        gravidade: gravidade === "" ? undefined : gravidade,
        ordenarPor: ordenarPor ?? undefined,
        ordem,
      })
      .then((corpo) => {
        definirAvisos(corpo.dados)
        definirNaoLidos(corpo.meta.nao_lidos)
        definirTotal(corpo.meta.total)
      })
      .catch((falha: unknown) => definirErro(falha instanceof Error ? falha.message : "falha"))
  }, [soNaoLidos, gravidade, pagina, porPagina, ordenarPor, ordem])

  useEffect(() => {
    carregar()
  }, [carregar])

  const avisarOSino = () => window.dispatchEvent(new CustomEvent("avisos-mudaram"))

  const marcar = async (id: number) => {
    const corpo = await api.marcarAvisoLido(id)
    definirNaoLidos(corpo.meta.nao_lidos)
    carregar()
    avisarOSino()
  }

  const marcarTodos = async () => {
    const corpo = await api.marcarTodosAvisosLidos()
    definirNaoLidos(corpo.meta.nao_lidos)
    carregar()
    avisarOSino()
  }

  const filtrado = soNaoLidos || gravidade !== ""
  const vazio = (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Sino />
        </EmptyMedia>
        <EmptyTitle>{filtrado ? "Nenhum aviso com estes filtros" : "Nenhum aviso"}</EmptyTitle>
      </EmptyHeader>
      {filtrado && (
        <EmptyContent>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              definirSoNaoLidos(false)
              definirGravidade("")
              definirPagina(1)
            }}
          >
            <Icones.Fechar />
            Limpar filtros
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )

  const ordenacao: SortingState = ordenarPor === null ? [] : [{ id: DA_TABELA[ordenarPor], desc: ordem === "desc" }]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meus avisos</CardTitle>
        <CardAction>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {naoLidos > 0 && <Selo situacao="critico">{plural(naoLidos, "não lido", "não lidos")}</Selo>}
            <label className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-input bg-card px-3 text-sm font-medium">
              <Checkbox
                checked={soNaoLidos}
                onCheckedChange={(marcado) => {
                  definirSoNaoLidos(marcado === true)
                  definirPagina(1)
                }}
              />
              Só não lidos
            </label>
            <Escolha
              rotulo="Filtrar pela gravidade"
              classeDaCaixa="w-52"
              valor={gravidade}
              aoMudar={(valor) => {
                definirGravidade(valor)
                definirPagina(1)
              }}
              vazio="Todas as gravidades"
              opcoes={GRAVIDADES}
            />
            <Button variant="outline" size="sm" type="button" disabled={naoLidos === 0} onClick={() => void marcarTodos()}>
              <Icones.ValidarTodos />
              Marcar todos como lidos
            </Button>
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        {erro !== null ? (
          <FalhaNaSecao mensagem={`Não foi possível carregar os avisos: ${erro}`} aoTentar={carregar} />
        ) : avisos === null ? (
          <EsqueletoDeTabela linhas={5} />
        ) : (
          <TablePrincipal2<AvisoNaTela>
            data={avisos}
            listrada
            pageCount={Math.max(1, Math.ceil(total / porPagina))}
            currentPage={pagina}
            pageSize={porPagina}
            totalRegistros={total}
            onPageChange={definirPagina}
            onPageSizeChange={(quantos) => {
              definirPorPagina(quantos)
              definirPagina(1)
            }}
            ordenacaoNoServidor
            ordenacao={ordenacao}
            onSortingChange={(nova) => {
              const primeira = nova[0]
              definirOrdenarPor(primeira === undefined ? null : (NA_TABELA[primeira.id] ?? null))
              definirOrdem(primeira?.desc === false ? "asc" : "desc")
              definirPagina(1)
            }}
            mensagemVazia={vazio}
          >
            <Column<AvisoNaTela>
              name="criado_em"
              label="Quando"
              enableSorting
              render={(_v, a) => (
                <span className={cn("whitespace-nowrap tabular-nums", a.lida_em === null ? "font-semibold text-foreground" : "text-muted-foreground")}>
                  {dataHora(a.criado_em)}
                </span>
              )}
            />
            <Column<AvisoNaTela>
              name="gravidade"
              label="Gravidade"
              enableSorting
              render={(_v, a) => <SeloDoValor valor={a.gravidade} />}
            />
            <Column<AvisoNaTela>
              name="contrato_numero"
              label="Contrato"
              enableSorting
              render={(_v, a) =>
                a.contrato_id !== null ? (
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1 font-semibold whitespace-nowrap text-primary hover:underline"
                    onClick={(evento) => {
                      evento.stopPropagation()
                      aoAbrirContrato(a.contrato_id as number)
                    }}
                  >
                    {a.contrato_numero ?? a.contrato_id}
                    <Icones.Abrir tamanho={12} />
                  </button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
            <Column<AvisoNaTela>
              name="assunto"
              label="Aviso"
              enableSorting={false}
              classe="min-w-[260px]"
              render={(_v, a) => (
                <span className="flex flex-col gap-0.5">
                  <span className={cn("text-foreground", a.lida_em === null ? "font-semibold" : "font-medium", MAIUSCULA)}>
                    {a.assunto.replace(/^\[ATHUS Contratos\]\s*/, "")}
                  </span>
                  <span className={cn("text-xs text-muted-foreground", MAIUSCULA)}>{a.corpo}</span>
                </span>
              )}
            />
            <Column<AvisoNaTela>
              name="lida_em"
              label="Ação"
              enableSorting={false}
              classe={COLUNA_DE_ACOES}
              render={(_v, a) =>
                a.lida_em === null ? (
                  <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={() => void marcar(a.id)}>
                    <Icones.Validar />
                    Marcar lido
                  </Button>
                ) : (
                  <span className="text-xs whitespace-nowrap text-muted-foreground">Lido em {dataHora(a.lida_em)}</span>
                )
              }
            />
          </TablePrincipal2>
        )}
      </CardContent>
    </Card>
  )
}
